import React, { createContext, useContext, useMemo } from 'react'

import { OrgRole, type OrganizationMembership } from 'pure-js/types/OrganizationMembership'

import useAppState from '../../hooks/useAppState'
import { isPlatformSuperUser } from './isPlatformSuperUser'
import { getActiveOrganizationId, getActiveOrgMembership } from './orgStateHelpers'

export type OrgTenantContextValue = {
  activeOrganizationId: string | undefined
  activeMembership: OrganizationMembership | undefined
  isOrgAdmin: boolean
  isOrgFacilitator: boolean
  isPlatformSuper: boolean
  canInviteMembers: boolean
  canManageInvites: boolean
}

const OrgTenantContext = createContext<OrgTenantContextValue | null>(null)

export function OrgTenantProvider({ children }: { children: React.ReactNode }) {
  const { state } = useAppState()
  const user = state.user

  const orgTenantContext = useMemo<OrgTenantContextValue>(() => {
    const activeOrganizationId = getActiveOrganizationId(user)
    const activeMembership = getActiveOrgMembership(user, activeOrganizationId)

    const isOrgAdmin = activeMembership?.role === OrgRole.ADMIN
    const isOrgFacilitator = activeMembership?.role === OrgRole.FACILITATOR
    const isPlatformSuper = isPlatformSuperUser(user)

    return {
      activeOrganizationId,
      activeMembership,
      isOrgAdmin,
      isOrgFacilitator,
      isPlatformSuper,
      canInviteMembers: isOrgAdmin || isOrgFacilitator || isPlatformSuper,
      canManageInvites: isOrgAdmin || isPlatformSuper
    }
  }, [user])

  return <OrgTenantContext.Provider value={orgTenantContext}>{children}</OrgTenantContext.Provider>
}

export function useOrgTenant(): OrgTenantContextValue {
  const ctx = useContext(OrgTenantContext)
  if (!ctx) {
    throw new Error('useOrgTenant must be used within OrgTenantProvider (org-scoped shell)')
  }
  return ctx
}
